// @flow
import _ from 'lodash';
import { KeyCode } from '../util/Constants';
import Store from '../store/Store';
import { KEYDOWN_CONFIGS } from '../store/Constants';
import KeydownConfig from './KeydownConfig';

const debug = require('../debug/debug')('wc:keyboard-shortcuts:KeyboardShortcuts');

function topLevelOnKeyDown(e: KeyboardEvent) {
  const keyCode = e.keyCode || e.which;
  const configs = Store.get(KEYDOWN_CONFIGS)[keyCode] || [];
  for (let i = configs.length - 1; i >= 0; i--) {
    const config: KeydownConfig = configs[i];
    const { handler, modifiers, checkTarget, shouldPreventDefault, shouldStopPropagation } = config;
    if (
      checkTarget &&
      _.includes(['input', 'textarea', 'select'], e.target.tagName.toLowerCase()) &&
      !_.isEqual('range', e.target.type ? e.target.type.toLowerCase() : '')
    ) {
      // checkTarget means ignore keyboard events when any type of input control is focused.
      continue;
    }
    if (_.isEmpty(modifiers)) {
      if (e.ctrlKey || e.shiftKey || e.metaKey) {
        //There are no modifiers but some key combination is pressed.
        continue;
      }
    } else {
      if (_.includes(modifiers, KeyCode.CONTROL) && !e.ctrlKey) {
        continue;
      }
      if (_.includes(modifiers, KeyCode.SHIFT) && !e.shiftKey) {
        continue;
      }
      if (_.includes(modifiers, KeyCode.META) && !e.metaKey) {
        continue;
      }
    }

    debug('[topLevelOnKeyDown()] calling handler, config: ', config);
    if (handler(e) === false) {
      // Handlers can return false if the event should continue to the next handler.
      debug('[topLevelOnKeyDown()] handler returned false, continuing iteration');
      continue;
    }

    if (shouldStopPropagation) {
      e.stopPropagation();
      if (e.stopImmediatePropagation) {
        e.stopImmediatePropagation();
      }
    }

    if (shouldPreventDefault) {
      e.preventDefault();
    }

    debug('[topLevelOnKeyDown()] handler did not return false. Done with event.');
    return;
  }

  debug('[topLevelOnKeyDown()] no handler for event: ', e);
}

export default class KeyboardShortcuts {
  static addTopLevelKeydownListener() {
    document.addEventListener('keydown', topLevelOnKeyDown);
  }

  static add(config: KeydownConfig) {
    const keydownConfigs = Store.get(KEYDOWN_CONFIGS);
    if (!config.validate()) {
      const err = new Error('Invalid KeydownConfig');
      err.message = JSON.stringify(config);
      throw err;
    }

    config.keyCodes.forEach((keyCode: number) => {
      if (!keydownConfigs[keyCode]) {
        keydownConfigs[keyCode] = [];
      }
      keydownConfigs[keyCode].push(config);
    });

    // Probably don't need to emit store change events but we'll do it anyway for consistency's sake
    Store.put(KEYDOWN_CONFIGS, keydownConfigs);
  }

  static remove(namespace: string) {
    const keydownConfigs = Store.get(KEYDOWN_CONFIGS);
    Object.keys(keydownConfigs).forEach((key) => {
      keydownConfigs[key] = keydownConfigs[key].filter((config: KeydownConfig) => {
        return config.namespace !== namespace;
      });
    });

    // Probably don't need to emit store change events but we'll do it anyway for consistency's sake
    Store.put(KEYDOWN_CONFIGS, keydownConfigs);
  }
}
